import { API_URL } from "~/lib/apiUrl";

export type UserInfo = { code?: number } | null;

export type UserData = {
  productSettings: Window["gazeDataPromises"]["productSettings"];
  programs: Window["gazeDataPromises"]["programs"];
  fileTree: Window["gazeDataPromises"]["fileTree"];
  userInfo: Promise<UserInfo>;
};

// The api answers /user/info with one of these codes for a user that has no username yet.
const NEEDS_USERNAME_CODES = [1012, 1022];

function hasNeedsUsernameCode(info: UserInfo): boolean {
  return info?.code !== undefined && NEEDS_USERNAME_CODES.includes(info.code);
}

/** An empty answer also sends the user to /set-username, as the React page did. */
export function needsUsername(info: UserInfo): boolean {
  return info === null || hasNeedsUsernameCode(info);
}

/** Sends the 4 requests that the web app needs at start. All 4 requests wait for one token. */
export function fetchUserData(token: Promise<string>): UserData {
  const get = (path: string) =>
    token.then((value) =>
      fetch(API_URL + path, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + value,
        },
      }),
    );

  const json = (what: string) => (res: Response) => {
    if (!res.ok) {
      throw new Error(`Failed to preload ${what}. Status: ${res.status}`);
    }
    return res.json();
  };

  // A call that names no key answers with every row whose key is not a
  // `/User/` path, so this file holds no copy of the product key list.
  const productSettings = get("/settings").then(json("product settings"));

  // Gaze parses the text. The parent does not parse it, because the answer
  // holds the schema of each program.
  const programs = get(
    "/ps?include_schemas=true&include_expressions=true&include_version_hash=true&share_import_schemas=true",
  ).then(async (res) => {
    if (!res.ok) {
      throw new Error(`Failed to preload programs. Status: ${res.status}`);
    }
    return res.text();
  });

  const userInfo = get("/user/info").then(async (res): Promise<UserInfo> => {
    const body = await res.text();
    let data: UserInfo = null;
    if (body) {
      try {
        data = JSON.parse(body);
      } catch {
        throw new Error(
          "Membrane could not read the user info. The API returned status " +
            res.status +
            ". " +
            body,
        );
      }
    }
    if (!res.ok && !hasNeedsUsernameCode(data)) {
      throw new Error(
        "Membrane could not read the user info. The API returned status " +
          res.status +
          ".",
      );
    }
    return data;
  });

  const fileTree = get("/fs/files").then(json("file tree"));

  // Each consumer awaits its promise much later, so a rejection reaches
  // `onunhandledrejection` before any handler exists. A no-op catch marks
  // the promise handled. The consumer that awaits it still reads the error.
  for (const promise of [productSettings, programs, userInfo, fileTree]) {
    promise.catch(() => {});
  }

  return { productSettings, programs, fileTree, userInfo };
}
